import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";
import CaseStudyTabs from "../components/CaseStudyTabs";
import { LaptopFrame, PhoneFrame } from "../components/DeviceMockups";

export default function Work() {
  return (
    <main data-testid="work-page">
      <Seo
        title="Selected Work | AT Digital"
        description="How AT Digital translated Dr. Tran College Consulting — a sophisticated college admissions and financial-aid practice — into a clear digital customer story."
      />
      <section className="relative overflow-hidden pb-16 pt-36 sm:pt-44">
        <div className="bg-blueprint absolute inset-0 opacity-40" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="font-mono text-xs font-semibold tracking-[0.3em] text-gold">SELECTED WORK</p>
            <h1 className="mt-6 max-w-3xl font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Every business has a story worth telling well.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-400">
              A look at how we turn complex businesses into clear digital experiences — starting with our featured
              client engagement.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="work-case-study" className="py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid grid-cols-1 items-start gap-16 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <Reveal>
                <div className="relative">
                  <div
                    className="absolute -inset-12 -z-10 opacity-70"
                    style={{ background: "radial-gradient(closest-side, rgba(37,99,235,0.14), transparent)" }}
                    aria-hidden="true"
                  />
                  <LaptopFrame
                    src="/assets/drtran-desktop.jpg"
                    alt="Dr. Tran College Consulting website — desktop homepage by AT Digital"
                  />
                  <PhoneFrame
                    src="/assets/drtran-mobile.jpg"
                    alt="Dr. Tran College Consulting website — mobile responsive view"
                    className="absolute -bottom-8 -right-2 w-28 sm:-right-6 sm:w-36"
                  />
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-6">
              <Reveal>
                <p className="font-mono text-[11px] font-medium tracking-[0.2em] text-royal">
                  WEBSITE DESIGN &amp; DEVELOPMENT
                </p>
                <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                  Dr. Tran College Consulting
                </h2>
              </Reveal>
              <Reveal delay={0.1} className="mt-8">
                <CaseStudyTabs />
              </Reveal>
              <Reveal delay={0.15} className="mt-10">
                <a
                  href="https://drtrancollegeconsulting.net/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="work-view-live-btn"
                  className="group inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-bold text-ink transition-colors duration-300 hover:bg-goldlight"
                >
                  View Live Site
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </a>
              </Reveal>
            </div>
          </div>

          <Reveal className="mt-24">
            <div className="flex flex-col items-start justify-between gap-8 rounded-3xl border border-dashed border-navyline bg-navycard/40 p-10 sm:p-12 lg:flex-row lg:items-center">
              <div>
                <p className="font-mono text-[11px] font-semibold tracking-[0.24em] text-gold">NEXT CASE STUDY</p>
                <h3 className="mt-3 font-display text-2xl font-bold text-white sm:text-3xl">
                  Your story could be next.
                </h3>
                <p className="mt-3 max-w-lg text-sm leading-relaxed text-slate-400">
                  We take on a small number of engagements at a time so every business gets our full attention. Tell
                  us yours.
                </p>
              </div>
              <Link
                to="/contact"
                data-testid="work-next-project-cta"
                className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-bold text-ink transition-colors duration-300 hover:bg-goldlight"
              >
                Tell Us Your Story
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
